import { useLogger } from '../adapters/ports.js';

import { AdministrationApiError } from './AdministrationApiError.js';
import { auctionHouseMethods } from './auctionHouseMethods.js';
import { auctionOperationMethods } from './auctionOperationMethods.js';
import { companyMethods } from './companyMethods.js';
import { dealershipMethods } from './dealershipMethods.js';
import { dealershipOperationMethods } from './dealershipOperationMethods.js';
import { placesMethods } from './placesMethods.js';
import { logRequestOutcome, normalizeRequestPath, requestTimestamp } from './requestLogging.js';

/**
 * The client for `administration-server`, which every Administration surface
 * reads through. See the README for the shape and what is deliberately absent.
 */

// Sent with no body AND no `Content-Type`: Fastify parses a body on a `DELETE`
// whenever one is declared, so announcing `application/json` and sending
// nothing earns `400 MALFORMED_REQUEST_BODY`. `PUT` stays out even though eight
// routes take one with no body — those send `{}`, and silencing `PUT` wholesale
// would strip the body off the fifteen that need one.
const METHODS_SENT_WITHOUT_CONTENT = new Set(['GET', 'DELETE']);

export function createAdministrationSdk() {
    let customHeaderFn = async function () { return {}; };
    let onUnauthorized = null;
    let baseAdministrationUri;

    /*****************************************************/
    /* Private Methods */
    /*****************************************************/
    /**
     * @param {string} path - Appended to `baseAdministrationUri`, leading slash included.
     * @param {Object} [query] - `undefined` and `null` entries are omitted, so a
     *   caller can pass optional parameters without composing the object first.
     *   An empty result appends no `?`.
     * @returns {string}
     */
    function buildUrl(path, query) {
        const url = `${baseAdministrationUri}${path}`;
        if (!query) {
            return url;
        }
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(query)) {
            if (value !== undefined && value !== null) {
                params.append(key, value);
            }
        }
        const search = params.toString();
        if (search === '') {
            return url;
        }
        return `${url}?${search}`;
    }

    /**
     * The one seam every request passes through. Throws on any non-2xx, and
     * calls `onUnauthorized` on a 401 — wrapped, so a handler that throws cannot
     * turn a 401 into a resolved promise.
     */
    async function sendRequest(url, method, body = {}) {
        const startedAt = requestTimestamp();
        const sendsContent = METHODS_SENT_WITHOUT_CONTENT.has(method) === false;
        // `Content-Type` is this client's to decide in both directions and a
        // host cannot move it either way: one supplied here would break every
        // JSON request from the one place nobody would look. Every other header
        // the hook returns stands, `Authorization` included.
        const headers = { ...await customHeaderFn({ method, url }) };
        for (const name of Object.keys(headers)) {
            if (name.toLowerCase() === 'content-type') {
                delete headers[name];
            }
        }
        if (sendsContent) {
            headers['Content-Type'] = 'application/json';
        }
        let response;
        try {
            response = await fetch(url, {
                method,
                mode: 'cors',
                cache: 'no-cache',
                credentials: 'same-origin',
                headers,
                redirect: 'follow',
                referrerPolicy: 'no-referrer',
                body: sendsContent ? JSON.stringify(body) : undefined
            });
        } catch (err) {
            logRequestOutcome({ method, url, startedAt, ok: false, err });
            throw err;
        }
        logRequestOutcome({ method, url, startedAt, status: response.status, ok: response.ok });
        if (response.ok === false) {
            let responseBody;
            const contentType = response.headers.get('content-type');
            if (contentType !== null && contentType.indexOf('application/json') !== -1) {
                responseBody = await response.json();
            }
            const err = new AdministrationApiError({
                method,
                url,
                statusCode: response.status,
                body: responseBody
            });
            if (response.status === 401) {
                // Logged whether or not a handler is installed: in the http
                // entries alone a dying session looks like a working app.
                useLogger().warn(
                    { event: 'auth.unauthorized', method, path: normalizeRequestPath(url) },
                    'request rejected as unauthorized'
                );
                if (typeof onUnauthorized === 'function') {
                    try {
                        onUnauthorized(err);
                    } catch (handlerError) {
                        useLogger().error(
                            { event: 'auth.unauthorized_handler_failed', err: handlerError },
                            'unauthorized handler threw'
                        );
                    }
                }
            }
            throw err;
        }
        const contentType = response.headers.get('content-type');
        if (contentType !== null && contentType.indexOf('application/json') !== -1) {
            return await response.json();
        }
        // Every 2XX this server declares carries JSON, so anything else is a
        // proxy answering in its place. Hand back its text rather than a parse
        // failure the caller cannot act on.
        return await response.text();
    }

    const seam = { sendRequest, buildUrl };

    /*****************************************************/
    /* Return Public API */
    /*****************************************************/
    return {
        sendRequest,

        // One object per noun, so a method name never has to stay unique across
        // five collections repeating the same nine verbs.
        companies: companyMethods(seam),
        auctionOperations: auctionOperationMethods(seam),
        dealershipOperations: dealershipOperationMethods(seam),
        auctionHouses: auctionHouseMethods(seam),
        dealerships: dealershipMethods(seam),

        // Not a noun. Address verification addresses no document at all, and it
        // sits here rather than under a noun because both a house's location
        // and a company's billing address are checked the same way.
        places: placesMethods(seam),

        // ---- Configuration -------------------------------------------
        //
        // Properties rather than a `configure()` call, matching the sibling SDK
        // and the event-runtime SDK, which a host configures a few lines apart.
        // All three are set once at boot, and each is checked on the way in so a
        // misconfiguration throws at the line that caused it rather than at the
        // next request.

        /** Headers merged into every request — an app sets this to supply `Authorization`. */
        get customHeaderFn() {
            return customHeaderFn;
        },
        set customHeaderFn(newCustomHeaderFn) {
            if (typeof newCustomHeaderFn !== 'function') {
                throw new TypeError('customHeaderFn must be a function returning headers');
            }
            customHeaderFn = newCustomHeaderFn;
        },
        /** Called with the error when a request 401s. */
        get onUnauthorized() {
            return onUnauthorized;
        },
        set onUnauthorized(newHandler) {
            // `null` is how an app clears the handler, so it is the one
            // non-function this accepts.
            if (newHandler !== null && typeof newHandler !== 'function') {
                throw new TypeError('onUnauthorized must be a function or null');
            }
            onUnauthorized = newHandler;
        },
        /**
         * The origin every URL is built against. May be relative — the dev
         * proxy's `/api` — which is why `buildUrl` does string work rather than
         * using `URL`, which rejects a relative base.
         */
        get baseAdministrationUri() {
            return baseAdministrationUri;
        },
        set baseAdministrationUri(newBaseAdministrationUri) {
            if (typeof newBaseAdministrationUri !== 'string' || newBaseAdministrationUri === '') {
                throw new TypeError('baseAdministrationUri must be a non-empty string');
            }
            baseAdministrationUri = newBaseAdministrationUri;
        }
    };
}

/**
 * The singleton the app configures once and every store reads through. The
 * factory is exported beside it because a test needs a client whose
 * configuration nothing else has touched, and a module-scoped singleton cannot
 * give it that without a module reset between every case.
 */
export const administrationApiSdk = createAdministrationSdk();

export default administrationApiSdk;
